import { safeExecute } from '@/utils/function.util';

export interface TicketDisplayInput {
  id: string;
  link?: string;
}

/**
 * Returns a human-readable ticket identifier for display.
 *
 * Linear stores the issue UUID in `id`, but the human-readable identifier
 * (e.g. `LAB-42`) is embedded in the ticket URL:
 *   https://linear.app/<workspace>/issue/LAB-42/<slug>
 * When `link` is a Linear issue URL we parse the identifier out of it;
 * otherwise (Jira, Azure DevOps, Freshdesk, etc. already store a readable
 * key in `id`) we return `id` unchanged.
 *
 * The returned value is for DISPLAY ONLY — `id` remains the stable key used
 * for dedup, disconnect, and React keys.
 */
export function getTicketDisplayId(ticket: TicketDisplayInput): string {
  const { id, link } = ticket;
  if (!link) return id;

  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return id;
  }

  // Only Linear embeds a readable identifier in its URL path.
  if (!/(^|\.)linear\.app$/i.test(url.hostname)) return id;

  // Two path shapes: canonical `/<workspace>/issue/<IDENTIFIER>/<slug?>` and
  // the short form `/i/<IDENTIFIER>`. Prefer the canonical match.
  const match =
    url.pathname.match(/\/issue\/([^/]+)/i) ??
    url.pathname.match(/\/i\/([^/]+)/i);
  const rawSegment = match?.[1] ?? '';
  // decodeURIComponent throws URIError on malformed percent-encoding
  // (e.g. "%E0%A4%A"); fall back to '' so it fails validation and we return id.
  const identifier = safeExecute(() => decodeURIComponent(rawSegment), '');

  // Validate it looks like a Linear identifier (TEAMKEY-NUMBER) before trusting it.
  return /^[A-Za-z0-9]+-\d+$/.test(identifier) ? identifier : id;
}
